import Button from 'ext-ui/components/Button/Button'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import ArrowIcon from 'ext-ui/svg/icon/ArrowIcon'
import React, { ReactElement } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { useTranslate } from 'src/localization/Trns'
import { virtualCardsUrl } from 'src/navigation/urls'

import style from './CancelSubscriptionDowngrade.style'
import VirtualCardLimitMeter from './VirtualCardLimitMeter'

// Starter plan caps active virtual cards at 100. We can't read this from the
// usages endpoint because it returns the *user's current plan's* entitlement
// (e.g. Pro = 500), so the Starter target is hardcoded here.
const STARTER_VIRTUAL_CARD_LIMIT = 100

interface CancelSubscriptionDowngradeProps {
  onConfirm: () => void
  onCancel: () => void
  billingCycleEnd: string | null
  usage: number | undefined
}

export default function CancelSubscriptionDowngrade(
  props: CancelSubscriptionDowngradeProps,
): ReactElement {
  const { onConfirm, onCancel, billingCycleEnd, usage } = props
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  // Default to over-limit treatment until the usages query settles so an
  // over-cap user can't click past the gate during the loading window.
  // Once `usage` arrives, switch to the real check.
  const withinStarterLimit =
    typeof usage === 'number' ? usage <= STARTER_VIRTUAL_CARD_LIMIT : false

  const handleManageCardsClick = (): void => {
    analytics.track('cancel subscription', {
      action: 'manage_cards_clicked',
    })
  }

  return (
    <div css={style} data-testid="cancel-step-downgrade">
      <div className="CancelSubscriptionDowngrade-content">
        <div className="CancelSubscriptionDowngrade-text">
          <h2 className="CancelSubscriptionDowngrade-header">
            {translate('Downgrade to Starter')}
          </h2>
          <p className="CancelSubscriptionDowngrade-body-text">
            {withinStarterLimit
              ? billingCycleEnd
                ? translate(
                    'Starter plans support up to ${entitlement} active virtual cards. Your downgrade will take effect on ${billingCycleEnd}.',
                    {
                      entitlement: String(STARTER_VIRTUAL_CARD_LIMIT),
                      billingCycleEnd,
                    },
                  )
                : translate(
                    'Starter plans support up to ${entitlement} active virtual cards. Your downgrade will take effect at the end of your billing cycle.',
                    { entitlement: String(STARTER_VIRTUAL_CARD_LIMIT) },
                  )
              : billingCycleEnd
                ? translate(
                    "Starter plans support up to ${entitlement} active virtual cards. To complete your downgrade, you'll need to deactivate cards until you're within the Starter limit before your billing cycle ends on ${billingCycleEnd}.",
                    {
                      entitlement: String(STARTER_VIRTUAL_CARD_LIMIT),
                      billingCycleEnd,
                    },
                  )
                : translate(
                    "Starter plans support up to ${entitlement} active virtual cards. To complete your downgrade, you'll need to deactivate cards until you're within the Starter limit before the end of your billing cycle.",
                    { entitlement: String(STARTER_VIRTUAL_CARD_LIMIT) },
                  )}
          </p>
        </div>

        {typeof usage === 'number' && (
          <VirtualCardLimitMeter
            usage={usage}
            entitlement={STARTER_VIRTUAL_CARD_LIMIT}
            withinLimit={withinStarterLimit}
          />
        )}

        {!withinStarterLimit && typeof usage === 'number' && (
          <a
            className="CancelSubscriptionDowngrade-manage-card"
            href={virtualCardsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleManageCardsClick}
            data-testid="cancel-downgrade-manage-cards"
          >
            <span className="CancelSubscriptionDowngrade-manage-card-text">
              {translate(
                'Go to your virtual cards to deactivate the ones you no longer need.',
              )}
            </span>
            <span className="CancelSubscriptionDowngrade-manage-card-cta">
              {translate('Manage Cards')}
              <ArrowIcon direction="right" aria-hidden="true" />
            </span>
          </a>
        )}

        <div
          className="CancelSubscriptionDowngrade-warning"
          data-testid="cancel-downgrade-warning"
        >
          <AlertTriangleYellowIcon
            aria-hidden="true"
            className="CancelSubscriptionDowngrade-warning-icon"
          />
          <p className="CancelSubscriptionDowngrade-warning-text">
            <span className="CancelSubscriptionDowngrade-warning-bold">
              {billingCycleEnd
                ? translate(
                    'Cards still over the limit on ${billingCycleEnd} will be automatically deactivated.',
                    { billingCycleEnd },
                  )
                : translate(
                    'Cards still over the limit at the end of your billing cycle will be automatically deactivated.',
                  )}
            </span>{' '}
            {translate(
              'Deactivated cards can be reactivated later if your organization upgrades again or returns within plan limits.',
            )}
          </p>
        </div>
      </div>

      <div className="CancelSubscriptionDowngrade-buttons">
        <Button
          variant="text"
          onClick={onCancel}
          data-testid="cancel-downgrade-cancel"
        >
          {translate('Cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          // Block advancement until we know the usage count. Covers both
          // the initial loading window and the case where /usages errors
          // or returns without ACTIVE_VIRTUAL_CARD_LIMIT — without this
          // an over-cap user could click straight through with no meter
          // visible.
          disabled={typeof usage !== 'number'}
          data-testid="cancel-downgrade-confirm"
        >
          {translate('Confirm')}
        </Button>
      </div>
    </div>
  )
}
